import {
	HubConnection,
	HubConnectionBuilder,
	HubConnectionState,
	LogLevel,
} from '@microsoft/signalr';

import authService from '../api/authService';
import { ChatThreadDto, OrganizationTalentChatThreadMessageDto } from '../api/chatService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const CHAT_HUB_URL = `${API_BASE_URL.replace(/\/$/, '')}/hubs/chat`;
const E2E_MOCK_USER_KEY = 'E2E_MOCK_USER';
const TOKEN_REFRESH_WAIT_MS = 1500;

const MESSAGE_RECEIVED_EVENT = 'ChatMessageReceived';
const THREAD_UPDATED_EVENT = 'ChatThreadUpdated';
const UNREAD_COUNT_UPDATED_EVENT = 'ChatUnreadCountUpdated';
const THREAD_READ_EVENT = 'ChatThreadRead';

/** @userstory US-CHAT-02 */
export interface ChatMessageReceivedEventDto {
	threadId: string;
	organizationId: string;
	talentUserId: string;
	contextType: string;
	contextId?: string | null;
	message: OrganizationTalentChatThreadMessageDto;
}

/** @userstory US-CHAT-02 */
export interface ChatUnreadCountChangedEventDto {
	viewerAuthorType: 'Organization' | 'Talent';
	threadId: string;
	totalUnreadCount: number;
}

/** @userstory US-CHAT-02 */
export interface ChatThreadReadEventDto {
	threadId: string;
	organizationId: string;
	talentUserId: string;
	readerAuthorType: 'Organization' | 'Talent';
	readAt: string;
	readMessageIds: string[];
}

interface ChatRealtimeLifecycleHandlers {
	onReconnecting?: (error?: Error) => void;
	onReconnected?: (connectionId?: string) => void;
	onClose?: (error?: Error) => void;
}

/** @userstory US-CHAT-02 */
export interface ChatRealtimeClient {
	connect: () => Promise<void>;
	disconnect: () => Promise<void>;
	joinOrganization: (organizationId: string) => Promise<void>;
	joinTalent: () => Promise<void>;
	joinCompanyThread: (organizationId: string, threadId: string) => Promise<void>;
	joinTalentThread: (threadId: string) => Promise<void>;
	onMessageReceived: (handler: (payload: ChatMessageReceivedEventDto) => void) => () => void;
	onThreadUpdated: (handler: (payload: ChatThreadDto) => void) => () => void;
	onUnreadCountUpdated: (
		handler: (payload: ChatUnreadCountChangedEventDto) => void,
	) => () => void;
	onThreadRead: (handler: (payload: ChatThreadReadEventDto) => void) => () => void;
}

const isE2EMockMode = (): boolean => {
	try {
		const raw = localStorage.getItem(E2E_MOCK_USER_KEY);
		if (!raw) {
			return false;
		}

		const parsed = JSON.parse(raw) as { isE2EMode?: boolean };
		return parsed.isE2EMode === true;
	} catch {
		return false;
	}
};

const waitForFreshAccessToken = async (): Promise<string> => {
	window.dispatchEvent(new CustomEvent('auth-token-refresh-needed'));
	await new Promise((resolve) => setTimeout(resolve, TOKEN_REFRESH_WAIT_MS));

	const refreshedToken = authService.getAccessToken();
	if (!refreshedToken || !authService.isTokenValid(refreshedToken, true)) {
		throw new Error('Unable to acquire a valid access token for chat realtime connection.');
	}

	return refreshedToken;
};

const getAccessToken = async (): Promise<string> => {
	if (isE2EMockMode()) {
		return '';
	}

	const accessToken = authService.getAccessToken();
	if (accessToken && authService.isTokenValid(accessToken, true)) {
		return accessToken;
	}

	return waitForFreshAccessToken();
};

const subscribe = <T>(
	connection: HubConnection,
	eventName: string,
	handler: (payload: T) => void,
): (() => void) => {
	connection.on(eventName, handler);
	return () => connection.off(eventName, handler);
};

/**
 * Creates a typed SignalR client for organization-talent chat realtime events.
 * @userstory US-CHAT-02
 */
const createChatRealtimeClient = (
	handlers: ChatRealtimeLifecycleHandlers = {},
): ChatRealtimeClient => {
	let joinedOrganizationId: string | null = null;
	let joinedTalentRoot = false;
	const joinedCompanyThreads = new Map<string, { organizationId: string; threadId: string }>();
	const joinedTalentThreads = new Set<string>();

	const connection = new HubConnectionBuilder()
		.withUrl(CHAT_HUB_URL, {
			accessTokenFactory: getAccessToken,
		})
		.withAutomaticReconnect()
		.configureLogging(LogLevel.Warning)
		.build();

	const rejoinGroups = async () => {
		if (joinedOrganizationId) {
			await connection.invoke('JoinOrganizationAsync', joinedOrganizationId);
		}

		if (joinedTalentRoot) {
			await connection.invoke('JoinTalentAsync');
		}

		const companyThreadJoins: Array<{ organizationId: string; threadId: string }> = [];
		joinedCompanyThreads.forEach((join) => {
			companyThreadJoins.push(join);
		});

		for (let index = 0; index < companyThreadJoins.length; index += 1) {
			const join = companyThreadJoins[index];
			await connection.invoke('JoinThreadAsCompanyAsync', join.organizationId, join.threadId);
		}

		const talentThreadIds: string[] = [];
		joinedTalentThreads.forEach((threadId) => {
			talentThreadIds.push(threadId);
		});

		for (let index = 0; index < talentThreadIds.length; index += 1) {
			const threadId = talentThreadIds[index];
			await connection.invoke('JoinThreadAsTalentAsync', threadId);
		}
	};

	if (handlers.onReconnecting) {
		connection.onreconnecting((error) => {
			handlers.onReconnecting?.(error as Error | undefined);
		});
	}

	if (handlers.onReconnected) {
		connection.onreconnected((connectionId) => {
			void rejoinGroups().finally(() => {
				handlers.onReconnected?.(connectionId);
			});
		});
	} else {
		connection.onreconnected(() => {
			void rejoinGroups();
		});
	}

	if (handlers.onClose) {
		connection.onclose((error) => {
			handlers.onClose?.(error as Error | undefined);
		});
	}

	return {
		connect: async () => {
			if (isE2EMockMode() || connection.state === HubConnectionState.Connected) {
				return;
			}

			if (
				connection.state === HubConnectionState.Connecting ||
				connection.state === HubConnectionState.Reconnecting
			) {
				return;
			}

			await connection.start();
		},

		disconnect: async () => {
			if (connection.state === HubConnectionState.Disconnected) {
				return;
			}

			await connection.stop();
		},

		joinOrganization: async (organizationId: string) => {
			joinedOrganizationId = organizationId;
			await connection.invoke('JoinOrganizationAsync', organizationId);
		},

		joinTalent: async () => {
			joinedTalentRoot = true;
			await connection.invoke('JoinTalentAsync');
		},

		joinCompanyThread: async (organizationId: string, threadId: string) => {
			joinedCompanyThreads.set(`${organizationId}:${threadId}`, { organizationId, threadId });
			await connection.invoke('JoinThreadAsCompanyAsync', organizationId, threadId);
		},

		joinTalentThread: async (threadId: string) => {
			joinedTalentThreads.add(threadId);
			await connection.invoke('JoinThreadAsTalentAsync', threadId);
		},

		onMessageReceived: (handler) =>
			subscribe<ChatMessageReceivedEventDto>(connection, MESSAGE_RECEIVED_EVENT, handler),

		onThreadUpdated: (handler) =>
			subscribe<ChatThreadDto>(connection, THREAD_UPDATED_EVENT, handler),

		onUnreadCountUpdated: (handler) =>
			subscribe<ChatUnreadCountChangedEventDto>(
				connection,
				UNREAD_COUNT_UPDATED_EVENT,
				handler,
			),

		onThreadRead: (handler) =>
			subscribe<ChatThreadReadEventDto>(connection, THREAD_READ_EVENT, handler),
	};
};

export default createChatRealtimeClient;
